class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    this.addEventListener('click', (evt) => {
      if (evt.target === this) this.close()
    })
    this.setHeaderCartIconAccessibility()
    this.openFromCartLink()
  }

  openFromCartLink() {
    const params = new URLSearchParams(window.location.search)
    if (params.get('cart') !== 'open') return

    params.delete('cart')
    const search = params.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
    )
    this.open()
  }

  setHeaderCartIconAccessibility() {
    const cartBubble = document.getElementById('cart-bubble')
    if (!cartBubble) return

    cartBubble.setAttribute('role', 'button')
    cartBubble.setAttribute('aria-haspopup', 'dialog')
    cartBubble.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartBubble)
    })
    cartBubble.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartBubble)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)

    document.querySelectorAll('quick-add-modal').forEach((modal) => {
      modal.classList.remove('is-active')
      modal.classList.add('hidden')
    })

    setTimeout(() => {
      this.classList.remove('opacity-0', 'invisible')
      this.classList.add('opacity-100', 'visible')

      const drawerPanel = document.getElementById('CartDrawer-Panel')
      if (drawerPanel) {
        drawerPanel.classList.remove('translate-x-full')
        drawerPanel.classList.add('translate-x-0')
      }

      this.initializeRecommendedProductsSlider()
      this.initializeFreeShippingSlider()
    })

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer')
        const focusElement = document.getElementById('CartDrawer-Close') || containerToTrapFocusOn
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true },
    )

    document.body.classList.add('overflow-hidden')
  }

  // Rotating info messages slider
  initializeFreeShippingSlider() {
    const usp = this.querySelector('[data-cart-usp]')

    if (this.uspInterval) {
      clearInterval(this.uspInterval)
      this.uspInterval = null
    }

    if (!usp) return

    const slides = usp.querySelectorAll('[data-cart-usp-slide]')
    const dots = usp.querySelectorAll('[data-cart-usp-dot]')
    if (slides.length <= 1) return

    let current = 0

    const show = (index) => {
      slides.forEach((slide, i) => {
        slide.classList.toggle('opacity-100', i === index)
        slide.classList.toggle('opacity-0', i !== index)
      })
      dots.forEach((dot, i) => {
        dot.classList.toggle('bg-black-200', i === index)
        dot.classList.toggle('bg-black-12', i !== index)
      })
    }

    show(0)
    this.uspInterval = setInterval(() => {
      current = (current + 1) % slides.length
      show(current)
    }, 4000)

    dots.forEach((dot, index) => {
      if (dot.dataset.uspBound === 'true') return
      dot.dataset.uspBound = 'true'
      dot.addEventListener('click', () => {
        current = index
        show(current)
        if (this.uspInterval) {
          clearInterval(this.uspInterval)
          this.uspInterval = setInterval(() => {
            current = (current + 1) % slides.length
            show(current)
          }, 4000)
        }
      })
    })
  }

  // Recommendations carousel
  initializeRecommendedProductsSlider() {
    const recs = this.querySelector('[data-cart-recs]')
    if (!recs) return

    const track = recs.querySelector('[data-cart-recs-track]')
    if (!track || track.dataset.recsInit === 'true') return
    track.dataset.recsInit = 'true'

    const prevButton = recs.querySelector('[data-cart-recs-prev]')
    const nextButton = recs.querySelector('[data-cart-recs-next]')

    const getStep = () => {
      const item = track.querySelector('[data-cart-recs-item]')
      if (!item) return track.clientWidth
      const styles = getComputedStyle(track)
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0
      return item.offsetWidth + gap
    }

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1
      prevButton?.toggleAttribute('disabled', track.scrollLeft <= 0)
      nextButton?.toggleAttribute('disabled', track.scrollLeft >= maxScroll)
    }

    prevButton?.addEventListener('click', () => track.scrollBy({ left: -getStep(), behavior: 'smooth' }))
    nextButton?.addEventListener('click', () => track.scrollBy({ left: getStep(), behavior: 'smooth' }))

    let ticking = false
    track.addEventListener('scroll', () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        update()
        ticking = false
      })
    })

    update()
  }

  close() {
    this.classList.remove('opacity-100', 'visible')
    this.classList.add('opacity-0', 'invisible')

    const drawerPanel = document.getElementById('CartDrawer-Panel')
    if (drawerPanel) {
      drawerPanel.classList.remove('translate-x-0')
      drawerPanel.classList.add('translate-x-full')
    }

    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')

    const quickAddModals = document.querySelectorAll('quick-add-modal')
    quickAddModals.forEach((modal) => {
      modal.classList.remove('hidden')
    })

    if (this.uspInterval) {
      clearInterval(this.uspInterval)
      this.uspInterval = null
    }
  }

  isOpen() {
    return this.classList.contains('opacity-100')
  }

  showOverlay() {
    const overlay = document.getElementById('CartDrawer-LoadingSpinner')
    if (!overlay) return
    overlay.classList.remove('opacity-0', 'invisible')
    overlay.classList.add('opacity-100', 'visible')
  }

  hideOverlay() {
    const overlay = document.getElementById('CartDrawer-LoadingSpinner')
    if (!overlay) return
    overlay.classList.remove('opacity-100', 'visible')
    overlay.classList.add('opacity-0', 'invisible')
  }

  updateDrawerFromHTML(html) {
    const parsed = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html

    const newItems = parsed.querySelector('#CartDrawer-CartItems')
    const currentItems = document.getElementById('CartDrawer-CartItems')
    if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML

    const newFooter = parsed.querySelector('#CartDrawer-Footer')
    const currentFooter = document.getElementById('CartDrawer-Footer')
    if (newFooter && currentFooter) currentFooter.innerHTML = newFooter.innerHTML

    const newRecs = parsed.querySelector('#CartDrawer-Recommendations')
    const currentRecs = document.getElementById('CartDrawer-Recommendations')
    if (newRecs && currentRecs && currentRecs.innerHTML !== newRecs.innerHTML) currentRecs.innerHTML = newRecs.innerHTML

    const newContainer = parsed.querySelector('#CartDrawer-Container')
    if (newContainer) this.classList.toggle('is-empty', newContainer.classList.contains('is-empty'))

    this.initializeRecommendedProductsSlider()
    this.initializeFreeShippingSlider()
  }

  updateCartBubble(html) {
    const parsed = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html
    const source = parsed.querySelector('.shopify-section')
    const target = document.getElementById('cart-bubble')
    if (source && target) target.innerHTML = source.innerHTML
  }

  renderContents(parsedState) {
    this.productId = parsedState.id
    const drawerHTML = parsedState.sections && parsedState.sections['cart-drawer']
    const bubbleHTML = parsedState.sections && parsedState.sections['cart-bubble']

    if (this.isOpen() && drawerHTML) {
      this.updateDrawerFromHTML(drawerHTML)
      if (bubbleHTML) this.updateCartBubble(bubbleHTML)
      return
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id)

      if (!sectionElement) return
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
    })

    setTimeout(() => {
      this.open()
    })
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-bubble',
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Footer',
      },
      {
        id: 'CartDrawer-Recommendations',
        section: 'cart-drawer',
        selector: '#CartDrawer-Recommendations',
      },
      {
        id: 'cart-bubble',
        section: 'cart-bubble',
        selector: '.shopify-section',
      },
    ]
  }

  enableLoading(line) {
    super.enableLoading(line)
    this.showLoading()
  }

  disableLoading(line) {
    super.disableLoading(line)
    this.hideLoading()
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line)
    this.isSelfUpdating = true
    const result = super.updateQuantity(line, quantity, name, variantId)
    if (result && typeof result.then === 'function') {
      return result
        .catch((error) => {
          console.error('Error updating cart:', error)
          this.disableLoading(line)
        })
        .finally(() => {
          this.isSelfUpdating = false
        })
    }
    this.isSelfUpdating = false
    return result
  }

  onCartUpdate(event) {
    const cartDrawer = document.getElementById('CartDrawer-Container')

    if (this.isSelfUpdating || (event && event.source === 'product-form')) {
      cartDrawer?.initializeRecommendedProductsSlider()
      cartDrawer?.initializeFreeShippingSlider()
      return Promise.resolve()
    }

    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        cartDrawer?.updateDrawerFromHTML(responseText)
      })
      .catch((e) => {
        console.error('Error updating cart drawer:', e)
      })
  }

  showLoading() {
    document.getElementById('CartDrawer-Container')?.showOverlay()
  }

  hideLoading() {
    document.getElementById('CartDrawer-Container')?.hideOverlay()
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)

class FreeGiftComponent extends HTMLElement {
  constructor() {
    super()
    this.currentLevel = this.getAttribute('current_level')
    this.isGiftAdded = this.getAttribute('_is-gift') === 'true'
    this.isPremiumAdded = this.getAttribute('_is-premium') === 'true'
    this.isGiftAvailable = this.getAttribute('isgiftavailable') === 'true'
    this.isPremiumAvailable = this.getAttribute('ispremiumavailable') === 'true'
    this.level2Threshold = parseInt(this.getAttribute('level_2_threshold'), 10)
    this.level3Threshold = parseInt(this.getAttribute('level_3_threshold'), 10)
    this.freeGift = this.getAttribute('free_gift')
    this.premiumGift1 = this.getAttribute('premium_gift_1')
  }
  connectedCallback() {
    this.checkCartState()
  }
  async checkCartState() {
    const cartTotal = parseInt(this.getAttribute('cart_total'), 10)

    const shouldFetchCart =
      (cartTotal < this.level2Threshold && this.isGiftAdded) || (cartTotal < this.level3Threshold && this.isPremiumAdded)

    let cartData = null
    const itemsToAdd = []
    const itemsToRemove = []

    // Add free gift
    if (cartTotal >= this.level2Threshold && !this.isGiftAdded && this.isGiftAvailable && this.shouldAddGift('gift')) {
      itemsToAdd.push({
        id: this.freeGift,
        quantity: 1,
        properties: { _is_gift: true },
      })
    }

    // Add premium gift
    if (cartTotal >= this.level3Threshold && !this.isPremiumAdded && this.isPremiumAvailable && this.shouldAddGift('premium')) {
      itemsToAdd.push({
        id: this.premiumGift1,
        quantity: 1,
        properties: { _is_premium: true },
      })
    }

    // Fetch cart only if needed
    if (shouldFetchCart) {
      cartData = await this.fetchUpdatedCart()
      if (!cartData) return

      // Remove free gift
      if (cartTotal < this.level2Threshold && this.isGiftAdded) {
        const freeGiftItem = cartData.items.find((item) => item.properties?._is_gift)
        if (freeGiftItem) {
          itemsToRemove.push({ id: freeGiftItem.key, quantity: 0 })
        }
      }

      // Remove premium gift
      if (cartTotal < this.level3Threshold && this.isPremiumAdded) {
        const premiumGiftItems = cartData.items.filter((item) => item.properties?._is_premium)
        premiumGiftItems.forEach((premiumGiftItem) => {
          itemsToRemove.push({ id: premiumGiftItem.key, quantity: 0 })
        })
      }
    }

    // Process additions
    if (itemsToAdd.length > 0) {
      await this.addGifts(itemsToAdd)
      await this.updateCartDrawer()
    }

    // Process removals
    let index = 0
    for (const item of itemsToRemove) {
      await this.removeGift(item)
      if (index === itemsToRemove.length - 1) {
        await this.updateCartDrawer()
      }
      index++
    }
  }
  shouldAddGift(type) {
    const timestamp = localStorage.getItem(type)
    if (timestamp) {
      const storedTime = new Date(timestamp)
      const currentTime = new Date()
      const timeDifference = (currentTime - storedTime) / 1000 / 60
      if (timeDifference < 30) {
        return false
      }
    }
    return true
  }
  async addGifts(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      })

      if (!response.ok) throw new Error('Failed to add gifts to the cart.')
      document.dispatchEvent(new Event('cart:added'))
    } catch (error) {
      console.error('Error adding gifts:', error)
    }
  }

  async removeGift(item) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to remove gift from the cart. Response: ${errorText}`)
      }
    } catch (error) {
      console.error('Error removing gift:', error)
    }
  }

  async fetchUpdatedCart() {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) throw new Error('Failed to fetch updated cart.')

      return await response.json()
    } catch (error) {
      console.error('Error fetching updated cart:', error)
      return null
    }
  }
  async fetchCartSection() {
    try {
      const response = await fetch('/?section_id=cart-drawer')
      if (!response.ok) throw new Error('Failed to fetch updated cart.')

      return await response.text()
    } catch (error) {
      console.error('Error fetching updated cart:', error)
      return null
    }
  }

  async updateCartDrawer() {
    const updatedCartHTML = await this.fetchCartSection()
    if (!updatedCartHTML) return

    const parser = new DOMParser()
    const updatedDocument = parser.parseFromString(updatedCartHTML, 'text/html')

    const cartDrawer = document.getElementById('CartDrawer-Container')
    if (!cartDrawer) {
      console.error('Cart drawer element not found.')
      return
    }

    const updatedCartItems = updatedDocument.getElementById('CartDrawer-Items')
    const currentCartItems = document.getElementById('CartDrawer-Items')
    if (updatedCartItems && currentCartItems) {
      currentCartItems.innerHTML = updatedCartItems.innerHTML
    }
    if (!updatedCartItems && currentCartItems) {
      currentCartItems.innerHTML = ''
    }
  }
}

customElements.define('free-gift', FreeGiftComponent)

class GiftRemoveButton extends HTMLElement {
  constructor() {
    super()
    this.type = this.dataset.type
    this.addEventListener('click', (event) => {
      event.preventDefault()
      this.storeTimestamp()
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items')
      cartItems.updateQuantity(this.dataset.index, 0)
    })
  }

  storeTimestamp() {
    if (this.type) {
      const timestamp = new Date().toISOString()
      localStorage.setItem(this.type, timestamp)
    } else {
      console.error('Type is not defined for GiftRemoveButton')
    }
  }
}

customElements.define('gift-remove-button', GiftRemoveButton)
